import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { toast, confirm as swalConfirm } from '../utils/swal';
import './InvitationCodeManager.css';

const InvitationCodeManager = ({ event, onBack }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(getEmptyForm());
  const [bulkForm, setBulkForm] = useState({
    prefix: '',
    count: 10,
    maxTicketsPerCode: 2,
    description: '',
    expiryDate: ''
  });

  function getEmptyForm() {
    return {
      code: '', assignedTo: '', description: '',
      maxTickets: 2, expiryDate: '', isActive: true
    };
  }

  useEffect(() => { loadCodes(); }, [event.id]);

  const loadCodes = async () => {
    setLoading(true);
    try {
      const data = await api.getInvitationCodes(event.id);
      setCodes(data);
    } catch (err) {
      console.error('Kodlar yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateRandomCode = () => {
    const prefix = event.name.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 4);
    const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
    setFormData(prev => ({ ...prev, code: `${prefix}${suffix}` }));
  };

  // Tekil kod kaydet
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim()) { toast.warning('Kod boş olamaz!'); return; }
    try {
      const codeData = {
        ...formData,
        eventId: event.id,
        maxTickets: parseInt(formData.maxTickets) || 0,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate).toISOString() : null
      };
      if (editingCode) {
        await api.updateInvitationCode(editingCode.id, codeData);
      } else {
        await api.createInvitationCode(codeData);
      }
      setShowModal(false);
      resetForm();
      loadCodes();
    } catch (err) {
      toast.error(`Hata: ${err.message}`);
    }
  };

  // Bulk kod oluştur
  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    const count = parseInt(bulkForm.count);
    if (!count || count < 1 || count > 500) {
      toast.warning('Kod sayısı 1 ile 500 arasında olmalıdır.');
      return;
    }
    setBulkLoading(true);
    try {
      const payload = {
        eventId: event.id,
        prefix: bulkForm.prefix.trim().toUpperCase(),
        count,
        maxTicketsPerCode: parseInt(bulkForm.maxTicketsPerCode) || 0,
        description: bulkForm.description,
        expiryDate: bulkForm.expiryDate ? new Date(bulkForm.expiryDate).toISOString() : null
      };
      const result = await api.bulkCreateCodes(payload);
      setShowBulkModal(false);
      setBulkForm({ prefix: '', count: 10, maxTicketsPerCode: 2, description: '', expiryDate: '' });
      loadCodes();
      toast.success(`${result.created} adet kod başarıyla oluşturuldu!`);
    } catch (err) {
      toast.error(`Hata: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      assignedTo: code.assignedTo || '',
      description: code.description || '',
      maxTickets: code.maxTickets,
      expiryDate: code.expiryDate ? new Date(code.expiryDate).toISOString().split('T')[0] : '',
      isActive: code.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (codeId) => {
    const del = await swalConfirm.delete('bu kodu');
      if (!del.isConfirmed) return;
    try {
      await api.deleteInvitationCode(codeId);
      loadCodes();
    } catch (err) {
      toast.error(`Hata: ${err.message}`);
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await api.updateInvitationCode(code.id, { ...code, isActive: !code.isActive });
      loadCodes();
    } catch (err) {
      toast.error(`Hata: ${err.message}`);
    }
  };

  const resetForm = () => { setFormData(getEmptyForm()); setEditingCode(null); };

  const stats = {
    total: codes.length,
    active: codes.filter(c => c.isActive).length,
    usedTickets: codes.reduce((sum, c) => sum + c.usedTickets, 0),
    assigned: codes.filter(c => c.assignedTo).length
  };

  const filteredCodes = codes.filter(c => {
    const term = searchTerm.toLowerCase();
    return (
      c.code.toLowerCase().includes(term) ||
      (c.assignedTo || '').toLowerCase().includes(term) ||
      (c.description || '').toLowerCase().includes(term)
    );
  });

  // Prefix önizlemesi
  const bulkPreview = bulkForm.prefix
    ? `${bulkForm.prefix.toUpperCase()}XXXXXX`
    : 'XXXXXX';

  return (
    <div className="invitation-code-manager">
      {/* Header */}
      <div className="manager-header">
        <button onClick={onBack} className="back-btn">← Geri</button>
        <div className="manager-title">
          <h2>🔑 Erişim Kodları</h2>
          <span className="event-name-sub">{event.name}</span>
        </div>
        <div className="header-actions">
          <button onClick={() => setShowBulkModal(true)} className="bulk-btn">
            ⚡ Toplu Oluştur
          </button>
          <button onClick={() => { resetForm(); setShowModal(true); }} className="add-code-btn">
            ➕ Tekil Kod
          </button>
        </div>
      </div>

      {/* İstatistikler */}
      <div className="code-stats-row">
        <div className="code-stat">
          <span className="code-stat-value">{stats.total}</span>
          <span className="code-stat-label">Toplam Kod</span>
        </div>
        <div className="code-stat">
          <span className="code-stat-value">{stats.active}</span>
          <span className="code-stat-label">Aktif</span>
        </div>
        <div className="code-stat">
          <span className="code-stat-value">{stats.usedTickets}</span>
          <span className="code-stat-label">Kullanılan Bilet</span>
        </div>
        <div className="code-stat">
          <span className="code-stat-value">{stats.assigned}</span>
          <span className="code-stat-label">Kişiye Atanmış</span>
        </div>
      </div>

      {/* Arama */}
      {codes.length > 3 && (
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Kod, isim veya not ile ara..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="clear-search">✕</button>
          )}
        </div>
      )}

      {/* Kod Listesi */}
      <div className="codes-container">
        {loading ? (
          <div className="loading-state">Yükleniyor...</div>
        ) : filteredCodes.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? (
              <p>"{searchTerm}" ile eşleşen kod bulunamadı.</p>
            ) : (
              <>
                <p>Henüz erişim kodu oluşturulmadı.</p>
                <div className="empty-actions">
                  <button onClick={() => setShowBulkModal(true)} className="bulk-btn-large">
                    ⚡ Toplu Oluştur
                  </button>
                  <button onClick={() => { resetForm(); setShowModal(true); }} className="create-first-btn">
                    ➕ Tekil Kod Oluştur
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="codes-list">
            <div className="codes-table-header">
              <span>Kod</span>
              <span>Kime Verildi</span>
              <span>Not</span>
              <span>Kullanım</span>
              <span>Durum</span>
              <span>İşlem</span>
            </div>

            {filteredCodes.map(code => {
              const isExpired = code.expiryDate && new Date(code.expiryDate) < new Date();
              const isFull = code.maxTickets > 0 && code.usedTickets >= code.maxTickets;
              const isEffectivelyActive = code.isActive && !isExpired && !isFull;

              return (
                <div key={code.id} className={`code-row ${!isEffectivelyActive ? 'inactive-row' : ''}`}>
                  <div className="code-cell code-value-cell">
                    <span className="code-text">{code.code}</span>
                    {isExpired && <span className="mini-badge expired">Süresi Doldu</span>}
                    {isFull && <span className="mini-badge full">Dolu</span>}
                    {!code.isActive && !isExpired && !isFull && (
                      <span className="mini-badge inactive">Pasif</span>
                    )}
                    {code.expiryDate && !isExpired && (
                      <span className="expiry-hint">
                        ⏰ {new Date(code.expiryDate).toLocaleDateString('tr-TR')}
                      </span>
                    )}
                  </div>

                  <div className="code-cell assigned-cell">
                    {code.assignedTo
                      ? <span className="assigned-name">👤 {code.assignedTo}</span>
                      : <span className="unassigned">—</span>}
                  </div>

                  <div className="code-cell note-cell">
                    {code.description
                      ? <span className="note-text" title={code.description}>{code.description}</span>
                      : <span className="unassigned">—</span>}
                  </div>

                  <div className="code-cell usage-cell">
                    <span className="usage-text">
                      {code.usedTickets} / {code.maxTickets > 0 ? code.maxTickets : '∞'}
                    </span>
                    {code.maxTickets > 0 && (
                      <div className="usage-bar">
                        <div
                          className="usage-fill"
                          style={{ width: `${Math.min(100, (code.usedTickets / code.maxTickets) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="code-cell status-cell">
                    <button
                      className={`toggle-btn ${code.isActive ? 'active' : 'passive'}`}
                      onClick={() => handleToggleActive(code)}
                      disabled={isExpired || isFull}
                    >
                      {code.isActive ? '✓ Aktif' : '✕ Pasif'}
                    </button>
                  </div>

                  <div className="code-cell actions-cell">
                    <button onClick={() => handleEdit(code)} className="icon-btn" title="Düzenle">✏️</button>
                    <button onClick={() => handleDelete(code.id)} className="icon-btn" title="Sil">🗑️</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== BULK MODAL ===== */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => !bulkLoading && setShowBulkModal(false)}>
          <div className="modal-content bulk-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚡ Toplu Kod Oluştur</h3>
              <button onClick={() => !bulkLoading && setShowBulkModal(false)} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleBulkSubmit} className="code-form">
              {/* Başlangıç Metni */}
              <div className="form-group">
                <label>Başlangıç Metni (Prefix)</label>
                <input
                  type="text"
                  value={bulkForm.prefix}
                  onChange={e => setBulkForm({ ...bulkForm, prefix: e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase() })}
                  placeholder="HAMLET, VIP, BASIN..."
                  maxLength="10"
                />
                <small className="hint">
                  Opsiyonel. Sadece harf ve rakam. Örnek kod: <strong>{bulkPreview}</strong>
                </small>
              </div>

              {/* Adet ve bilet yan yana */}
              <div className="form-row-two">
                <div className="form-group">
                  <label>Kod Adedi *</label>
                  <input
                    type="number"
                    value={bulkForm.count}
                    onChange={e => setBulkForm({ ...bulkForm, count: e.target.value })}
                    min="1"
                    max="500"
                    required
                  />
                  <small className="hint">Maks. 500</small>
                </div>

                <div className="form-group">
                  <label>Kod Başına Bilet Hakkı</label>
                  <input
                    type="number"
                    value={bulkForm.maxTicketsPerCode}
                    onChange={e => setBulkForm({ ...bulkForm, maxTicketsPerCode: e.target.value })}
                    min="0"
                  />
                  <small className="hint">0 = Sınırsız</small>
                </div>
              </div>

              {/* Not */}
              <div className="form-group">
                <label>Tüm Kodlara Not (opsiyonel)</label>
                <input
                  type="text"
                  value={bulkForm.description}
                  onChange={e => setBulkForm({ ...bulkForm, description: e.target.value })}
                  placeholder="Sponsorlar için, basın kartı..."
                />
              </div>

              {/* Son kullanma */}
              <div className="form-group">
                <label>Son Kullanma Tarihi (opsiyonel)</label>
                <input
                  type="date"
                  value={bulkForm.expiryDate}
                  onChange={e => setBulkForm({ ...bulkForm, expiryDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Özet */}
              <div className="bulk-summary">
                <strong>📋 Özet:</strong>{' '}
                <span>
                  {bulkForm.count || 0} adet kod oluşturulacak
                  {bulkForm.prefix && ` • Prefix: ${bulkForm.prefix.toUpperCase()}`}
                  {` • ${bulkForm.maxTicketsPerCode > 0 ? `Her kod ${bulkForm.maxTicketsPerCode} biletlik` : 'Sınırsız bilet hakkı'}`}
                </span>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => !bulkLoading && setShowBulkModal(false)}
                  className="cancel-btn"
                  disabled={bulkLoading}
                >
                  İptal
                </button>
                <button type="submit" className="submit-btn" disabled={bulkLoading}>
                  {bulkLoading
                    ? `⏳ Oluşturuluyor...`
                    : `⚡ ${bulkForm.count || 0} Kod Oluştur`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== TEKİL KOD MODAL ===== */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCode ? '✏️ Kodu Düzenle' : '➕ Yeni Erişim Kodu'}</h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="code-form">
              <div className="form-group">
                <label>Erişim Kodu *</label>
                <div className="code-input-group">
                  <input
                    type="text"
                    value={formData.code}
                    onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="HAMLET2026"
                    maxLength="20"
                    required
                  />
                  <button type="button" onClick={generateRandomCode} className="generate-btn">🎲</button>
                </div>
                <small className="hint">Büyük harf, tire ve boşluk olmadan</small>
              </div>

              <div className="form-group">
                <label>Kime Verildi</label>
                <input
                  type="text"
                  value={formData.assignedTo}
                  onChange={e => setFormData({ ...formData, assignedTo: e.target.value })}
                  placeholder="Ahmet Yılmaz, VIP misafir..."
                />
              </div>

              <div className="form-group">
                <label>Ek Not</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Sponsorlar için, basın kartı..."
                />
              </div>

              <div className="form-row-two">
                <div className="form-group">
                  <label>Maksimum Bilet</label>
                  <input
                    type="number"
                    value={formData.maxTickets}
                    onChange={e => setFormData({ ...formData, maxTickets: e.target.value })}
                    min="0"
                  />
                  <small className="hint">0 = Sınırsız</small>
                </div>

                <div className="form-group">
                  <label>Son Kullanma</label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={e => setFormData({ ...formData, expiryDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <span>Kod Aktif</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="cancel-btn">
                  İptal
                </button>
                <button type="submit" className="submit-btn">
                  {editingCode ? '✓ Güncelle' : '✓ Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationCodeManager;